import { getLayer } from '../core/model/document';
import type { AppContext } from './context';

/**
 * Async work that ends in a commit must refuse to revert newer work.
 *
 * The adjustment session has guarded itself since it shipped: it stores the
 * decode-time revision and drops itself the moment the document moves, because
 * a commit built from a captured snapshot silently reverts everything that
 * landed inside the await window — the worst kind of failure, a successful
 * toast over destroyed work. Seven other await→commit paths had the same
 * window and no guard. This is the adjust pattern, shared.
 *
 * Two flavours, matched to what the await actually invalidates:
 *
 *  - **`beginDocumentEdit`** for whole-document rebuilds (merges, transforms,
 *    apply-mask): the result is assembled from a captured `doc`, so *any*
 *    commit in the window invalidates it.
 *  - **`beginLayerEdit`** for strokes, shapes and fills: the pixels are baked
 *    from a captured source and applied to a freshly read document, so only
 *    the aimed-at layer matters. If it still exists and still holds the
 *    source the stroke was read from, the bake lands on the right base — an
 *    undo (or any commit) elsewhere does not invalidate it.
 *
 * The predicate is evaluated once, after the await, immediately before the
 * commit. A stale edit refuses with `STALE_EDIT_MESSAGE` and changes nothing;
 * undo remains the user's tool, not something we race against.
 */

export const STALE_EDIT_MESSAGE = 'The document changed while that was running — try again.';

/** Guard a whole-document rebuild: refuse if any commit landed since the capture. */
export function beginDocumentEdit(ctx: AppContext): () => boolean {
  const revision = ctx.store.getState().revision;
  return () => ctx.store.getState().revision !== revision;
}

interface LayerEditTarget {
  layerId: string;
  /** Which field the captured source belongs to: the layer's pixels or its mask. */
  field: 'pixels' | 'src' | 'mask';
  /** The content source the edit was based on. */
  src: string;
}

/** Guard a layer-scoped edit: refuse only if this layer (or its mask) moved. */
export function beginLayerEdit(ctx: AppContext, target: LayerEditTarget): () => boolean {
  return () => {
    const layer = getLayer(ctx.store.getState().doc, target.layerId);
    if (!layer) return true;
    if (target.field === 'mask') return layer.mask?.src !== target.src;
    if (layer.type === 'paint') return layer.pixels !== target.src;
    if (layer.type === 'image') return layer.src !== target.src;
    return true;
  };
}

/** One-shot form for the pointer hooks, which evaluate once, after their bake. */
export function layerEditStale(ctx: AppContext, target: LayerEditTarget): boolean {
  return beginLayerEdit(ctx, target)();
}
